/* eslint-disable */
// VENDORED VERBATIM from the MMEC TradingView overlay (Pepsi repo, src/strikeIv.js @ 594c8e5).
// This is the level engine the desktop tool runs; do not edit here — re-copy from the overlay repo.
/**
 * src/strikeIv.js
 * Per-strike IV scanning: turns each option-chain fetch into a "scan" of every
 * near-the-money strike's IV, and from the scan history derives
 *   - per-strike IV moves and how unusual they are (z vs that strike's own
 *     recent moves)            → the strike IV lane and strike spike callouts
 *   - an IV "pulse": the share of strikes moving irregularly in a scan
 *                               → the pulse monitor and its sound
 *   - the at-the-money IV of every scan
 *                               → an intraday IV series the app records itself
 *
 * A fetch only counts as a scan when the chain's IVs actually changed: the
 * backend caches the chain for 2–3 minutes, and repeated identical payloads
 * must not read as calm beats.
 */

export const SENSITIVITY_Z = { low: 3, med: 2.5, high: 2 };
export const MIN_MOVES_FOR_Z = 5; // a strike needs this many past moves before its z counts
export const HISTORY_SCANS = 20;
export const Z_SD_FLOOR = 0.15; // vol points — stops a near-silent strike's tiny sd from exploding z
export const STRIKE_WINDOW = 0.10; // outer bound, ±10% of spot
export const STRIKES_PER_SIDE = 15; // nearest OTM strikes kept on each side
export const PULSE_LEVELS = [
  { key: "low", min: 0, label: "LOW", color: "#94a3b8" },
  { key: "medium", min: 25, label: "MEDIUM", color: "#fbbf24" },
  { key: "high", min: 55, label: "HIGH", color: "#f87171" },
  { key: "unstable", min: 80, label: "UNSTABLE", color: "#f472b6" },
];
const LEVEL_RANK = { low: 0, medium: 1, high: 2, unstable: 3 };

const numOr = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

function expiryYmd(raw) {
  const s = String(raw || "").trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return m[1] + m[2] + m[3];
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) return String(+m[3] < 100 ? 2000 + +m[3] : +m[3]) + m[1].padStart(2, "0") + m[2].padStart(2, "0");
  return "";
}

/**
 * One scan's strike IVs from chain rows: the nearest quoted expiry, the 15
 * strikes nearest spot on each side (within ±10%), out-of-the-money side (puts below spot, calls above),
 * IV in vol points. Returns { expiry, points: [{k, type, name, iv}] }.
 */
// ---------------------------------------------------------------------------
// Own IV from prices. The feed's per-strike IV is computed against its
// "Underlying Price", which can be stale relative to the option quotes (seen
// pre-open: spot 7706 while put-call parity priced the index at ~7664). That
// turned ATM calls into "below intrinsic" (IV 0, delta 1) and inflated ATM
// puts, drawing a cliff at spot instead of a smile. Solving IV ourselves from
// mid prices against the parity forward is immune to that.
// ---------------------------------------------------------------------------

function normCdf(x) {
  // Abramowitz–Stegun 7.1.26 (|err| < 1.5e-7)
  const t = 1 / (1 + 0.3275911 * Math.abs(x) / Math.SQRT2);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-(x * x) / 2);
  return x >= 0 ? (1 + y) / 2 : (1 - y) / 2;
}

/** Black-76 price (no discounting — intraday horizons). */
export function black76(F, K, T, sigma, isCall) {
  const sT = sigma * Math.sqrt(T);
  const d1 = (Math.log(F / K) + 0.5 * sT * sT) / sT, d2 = d1 - sT;
  return isCall ? F * normCdf(d1) - K * normCdf(d2) : K * normCdf(-d2) - F * normCdf(-d1);
}

/** Implied vol (decimal) from a price by bisection; null when no vol fits. */
export function impliedVol(price, F, K, T, isCall) {
  if (!(price > 0) || !(F > 0) || !(K > 0) || !(T > 0)) return null;
  const intrinsic = Math.max(0, isCall ? F - K : K - F);
  if (price <= intrinsic + 1e-6) return null;
  let lo = 0.005, hi = 5;
  if (black76(F, K, T, hi, isCall) < price) return null;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (black76(F, K, T, mid, isCall) > price) hi = mid; else lo = mid;
  }
  return (lo + hi) / 2;
}

/** Years from `nowMs` to 16:00 New York on expiry YYYYMMDD (floored at 5 min). */
export function yearsToExpiry(ymd, nowMs = Date.now()) {
  const y = +ymd.slice(0, 4), m = +ymd.slice(4, 6) - 1, d = +ymd.slice(6, 8);
  // New York's UTC offset on that date (EDT −4 / EST −5).
  const nyNoon = +new Date(Date.UTC(y, m, d, 12)).toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", hour12: false });
  const closeUtc = Date.UTC(y, m, d, 16 + (12 - nyNoon));
  return Math.max(5 * 60e3, closeUtc - nowMs) / (365 * 24 * 3600e3);
}

const midOf = (r) => {
  const m = numOr(r?.["Mid"] ?? r?.mid);
  if (m != null && m > 0) return m;
  const b = numOr(r?.["Bid"] ?? r?.bid), a = numOr(r?.["Ask"] ?? r?.ask);
  return b != null && a != null && a > 0 && a >= b ? (a + b) / 2 : null;
};

/**
 * Forward from put-call parity (F = K + C − P), median over strikes within
 * ±1% of the quoted spot that have both mids. null when too few pairs.
 */
export function parityForward(pairs, spot) {
  const f = [];
  for (const [k, p] of pairs) {
    if (Math.abs(k - spot) > spot * 0.01) continue;
    const c = midOf(p.C), q = midOf(p.P);
    if (c != null && q != null) f.push(k + c - q);
  }
  if (f.length < 3) return null;
  f.sort((a, b) => a - b);
  return f[f.length >> 1];
}

/**
 * The chain carries IV under several names, and "Implied Volatility" is 0 on
 * the stable feed while "Implied Vol" / "implied_vol" hold the value. First
 * positive one wins; 0 means "no quote", never a real IV.
 */
function rowIv(r) {
  for (const k of ["Implied Volatility", "Implied Vol", "implied_vol", "implied_volatility", "iv"]) {
    const v = numOr(r[k]);
    if (v != null && v > 0) return v < 3 ? v * 100 : v; // decimals → vol points
  }
  return null;
}

export const MIN_POINTS_PER_EXPIRY = 5;

export function extractStrikeIv(rows, spot, nowMs = Date.now()) {
  if (!Array.isArray(rows) || !rows.length || !(spot > 0)) return { expiry: "", points: [] };
  const lo = spot * (1 - STRIKE_WINDOW), hi = spot * (1 + STRIKE_WINDOW);
  const chains = new Map(); // ymd -> Map(strike -> {C,P})
  for (const r of rows) {
    const e = expiryYmd(r["Expiry Date"] ?? r.expiry_date ?? r.expiry);
    if (!e) continue;
    const k = numOr(r["Strike"] ?? r.strike);
    if (k == null || k < lo || k > hi) continue;
    const type = String(r["Option Type"] ?? r.option_type ?? "").toUpperCase().startsWith("C") ? "C" : "P";
    if (!chains.has(e)) chains.set(e, new Map());
    const byK = chains.get(e);
    if (!byK.has(k)) byK.set(k, {});
    byK.get(k)[type] = r;
  }
  const build = (e) => {
    const byK = chains.get(e), pairs = [...byK.entries()];
    const F = parityForward(pairs, spot);
    const T = yearsToExpiry(e, nowMs);
    const pts = [];
    for (const [k, p] of pairs) {
      let type, iv = null;
      if (F) {
        // Own IV from the out-of-the-money mid, against the parity forward.
        type = k < F ? "P" : "C";
        const v = impliedVol(midOf(p[type]), F, k, T, type === "C");
        if (v != null) iv = v * 100;
      } else {
        // No two-sided quotes to solve from: fall back to the feed's IV.
        type = k < spot ? "P" : "C";
        iv = p[type] ? rowIv(p[type]) : null;
      }
      if (iv != null && iv > 0.5 && iv < 400) pts.push({ k, type, name: k + type, iv });
    }
    return { F, pts };
  };
  // Nearest expiry that is actually quoted (before the open, today's 0DTE
  // rows can carry nothing usable).
  for (const e of [...chains.keys()].sort()) {
    const { F, pts } = build(e);
    if (pts.length < MIN_POINTS_PER_EXPIRY) continue;
    const c = F || spot;
    // The STRIKES_PER_SIDE nearest strikes on each side of the forward.
    const near = (side) => pts.filter(side).sort((a, b) => Math.abs(a.k - c) - Math.abs(b.k - c)).slice(0, STRIKES_PER_SIDE);
    const points = [...near((p) => p.k < c), ...near((p) => p.k >= c)].sort((a, b) => a.k - b.k);
    return { expiry: e, points, forward: F, source: F ? "parity" : "feed" };
  }
  return { expiry: "", points: [] };
}
/**
 * Off-smile score per strike, available from the FIRST scan: fit
 * IV = a + b·x + c·x² with x = ln(K/S) (a skewed smile), then express each
 * strike's residual in robust sigmas (1.4826 × MAD, floored so a very smooth
 * chain doesn't turn noise into outliers). Returns Map(name -> zs), empty when
 * there are too few strikes to fit.
 */
export const SMILE_MIN_POINTS = 7;
export const SMILE_SD_FLOOR = 0.25; // vol points
export function smileResiduals(points, spot) {
  const out = new Map();
  if (points.length < SMILE_MIN_POINTS || !(spot > 0)) return out;
  // Normal equations for a quadratic least-squares fit.
  const S = [0, 0, 0, 0, 0], T = [0, 0, 0];
  for (const p of points) {
    const x = Math.log(p.k / spot);
    const pw = [1, x, x * x, x * x * x, x * x * x * x];
    for (let i = 0; i < 5; i++) S[i] += pw[i];
    T[0] += p.iv; T[1] += p.iv * x; T[2] += p.iv * x * x;
  }
  const A = [[S[0], S[1], S[2]], [S[1], S[2], S[3]], [S[2], S[3], S[4]]];
  const det = (m) => m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  const D = det(A);
  if (!Number.isFinite(D) || Math.abs(D) < 1e-18) return out;
  const coef = [0, 1, 2].map((c) => det(A.map((row, r) => row.map((v, j) => (j === c ? T[r] : v)))) / D);
  const res = points.map((p) => { const x = Math.log(p.k / spot); return p.iv - (coef[0] + coef[1] * x + coef[2] * x * x); });
  const med = (a) => { const s = [...a].sort((u, v) => u - v); return s[s.length >> 1]; };
  const m = med(res);
  const sd = Math.max(1.4826 * med(res.map((r) => Math.abs(r - m))), SMILE_SD_FLOOR);
  points.forEach((p, i) => out.set(p.name, (res[i] - m) / sd));
  return out;
}

/** ATM IV of a scan: mean IV of the two strikes nearest spot. */
export function atmIv(points, spot) {
  if (!points.length) return null;
  const near = [...points].sort((a, b) => Math.abs(a.k - spot) - Math.abs(b.k - spot)).slice(0, 2);
  return near.reduce((s, p) => s + p.iv, 0) / near.length;
}

export function levelOf(score) {
  let lv = PULSE_LEVELS[0];
  for (const l of PULSE_LEVELS) if (score >= l.min) lv = l;
  return lv;
}

/** Shared instance: poll.js feeds it every chain fetch, ivTrack.js reads it. */
export let strikeScanner = null;

/** Stateful scanner for one symbol + expiry. */
export class StrikeScanner {
  constructor({ sensitivity = "med" } = {}) {
    this.sensitivity = sensitivity;
    this.reset();
  }
  reset() {
    this.key = "";
    this.last = new Map(); // name -> iv at previous scan
    this.moves = new Map(); // name -> [Δiv ...] (most recent last)
    this.scans = 0;
    this.lastSig = "";
    this.pulseHistory = []; // [{t, score, level}]
    this.atmSeries = []; // [[t, atmIv]]
    this.latest = null;
  }
  /**
   * Feed one chain fetch. Returns the new scan result, or null when the chain
   * is unchanged since the last scan (backend cache) or empty.
   */
  ingest(rows, spot, { symbol = "", nowSec = Math.floor(Date.now() / 1000), scale = 1 } = {}) {
    const ext = extractStrikeIv(rows, spot, nowSec * 1000);
    const { expiry, points } = ext;
    const centre = ext.forward || spot; // where the market prices the index
    if (!points.length) return null;
    const key = symbol + "|" + expiry;
    if (key !== this.key) { this.reset(); this.key = key; }
    const sig = points.map((p) => p.name + ":" + p.iv.toFixed(4)).join(",");
    if (sig === this.lastSig) return null;
    this.lastSig = sig;
    this.scans++;

    const kz = SENSITIVITY_Z[this.sensitivity] ?? SENSITIVITY_Z.med;
    const smile = smileResiduals(points, centre);
    const items = [];
    let scored = 0, irregular = 0;
    for (const p of points) {
      const prev = this.last.get(p.name);
      const hist = this.moves.get(p.name) || [];
      let move = null, z = null;
      if (prev != null) {
        move = p.iv - prev;
        if (hist.length >= MIN_MOVES_FOR_Z) {
          const mean = hist.reduce((s, x) => s + x, 0) / hist.length;
          const sd = Math.sqrt(hist.reduce((s, x) => s + (x - mean) ** 2, 0) / hist.length);
          z = (move - mean) / Math.max(sd, Z_SD_FLOOR);
          scored++;
          if (Math.abs(z) >= kz) irregular++;
        }
        hist.push(move);
        if (hist.length > HISTORY_SCANS) hist.shift();
        this.moves.set(p.name, hist);
      }
      this.last.set(p.name, p.iv);
      const zs = smile.has(p.name) ? smile.get(p.name) : null;
      items.push({ k: p.k * scale, strike: p.k, type: p.type, name: p.name, iv: p.iv, move, z, zs });
    }

    const atm = atmIv(points, centre);
    if (atm != null) {
      this.atmSeries.push([nowSec, atm]);
      if (this.atmSeries.length > 2000) this.atmSeries.shift();
    }

    // Two signals, either of which is irregularity:
    //   off-smile  — strike IV far from the fitted smile (from the first scan)
    //   move       — strike IV move unusual for that strike (after warm-up)
    const warm = scored > 0;
    const offSmile = items.filter((i) => i.zs != null && Math.abs(i.zs) >= kz).length;
    const smileN = items.filter((i) => i.zs != null).length;
    const share = Math.max(smileN ? offSmile / smileN : 0, warm ? irregular / scored : 0);
    const hasReading = smileN > 0 || warm;
    const score = hasReading ? Math.min(100, Math.round(share * 200)) : null;
    let level = hasReading ? levelOf(score) : null;
    if (hasReading) {
      // Three HIGH-or-worse scans in a row is instability even below 80.
      const recent = this.pulseHistory.slice(-2).map((h) => h.level);
      if (level.key === "high" && recent.length === 2 && recent.every((l) => LEVEL_RANK[l] >= 2))
        level = PULSE_LEVELS[3];
      this.pulseHistory.push({ t: nowSec, score, level: level.key });
      if (this.pulseHistory.length > 60) this.pulseHistory.shift();
    }
    const strength = (i) => Math.max(Math.abs(i.z ?? 0), Math.abs(i.zs ?? 0));
    const spikes = items
      .filter((i) => strength(i) >= kz)
      .map((i) => ({ ...i, reason: Math.abs(i.z ?? 0) >= Math.abs(i.zs ?? 0) ? 'move' : 'smile' }))
      .sort((a, b) => strength(b) - strength(a));
    this.latest = {
      t: nowSec, expiry, forward: ext.forward || null, ivSource: ext.source, scans: this.scans, warm, warmNeed: MIN_MOVES_FOR_Z + 1, hasReading,
      score, level, scored, irregular, offSmile, smileN, total: points.length, kz,
      items, spikes: spikes.slice(0, 6),
      pulse: this.pulseHistory.slice(-30),
    };
    return this.latest;
  }
}

strikeScanner = new StrikeScanner();